import React, { useEffect, useState, useRef } from "react"
import { Prompt } from "react-router"
import PropTypes from "prop-types"
import classNames from "classnames"
import Tooltip from "@material-ui/core/Tooltip"
import { makeStyles } from "@material-ui/core/styles"
import styles from "./CompanyUserInfo.module.scss"
import UserImage from "../UserImage/UserImage"
import Input from "../Input/Input"
import Dropdown from "../Dropdown/Dropdown"
import MultiSelectDropdown from "../MultiSelectDropdown"
import { TagFinderView } from "../TagFinderView"
import { businessClassificationOptions } from "./options/businessClassificationOptions"
import { options as companyRoleTypeOptions } from "../filters/MemberSearchFilter/wrappers/CompanyRoleTypeDropdown/options"
import { options as companyIndustryTypeOptions } from "../filters/MemberSearchFilter/wrappers/CompanyIndustryTypeDropdown/options"
import { filterOptions as activityFilterOptions } from "../filters/MemberSearchFilter/wrappers/CompanyActivityTypeDropdown/options"
import { diverseOptions } from "./options/diverseOptions"
import { marketOptions } from "./options/marketOptions"
import { decriptionValidation, nameValidaton, sectorValidation, industryValidation } from "./validation"
import { objectDeepCopy } from "../../helpers"
import CompanyUserShowInfo from "./CompanyUserShowInfo"
import ConfirmCancelPopup from "../Popup/ConfirmCancelPopup"
import TextArea from "../TextArea/TextArea"
import SaveCancelButtons from "./SaveCancelButtons"
import { messages } from "../../helpers/constants"
import ReportButton from "../ReportButton/ReportButton"

const useTooltipStyles = makeStyles(() => ({
  arrow: {
    color: "rgb(21,21,21)",
  },
  tooltip: {
    backgroundColor: "rgb(21,21,21)",
    color: "#FFFFFF",
    fontSize: "12px",
    padding: "8px 12px",
    maxWidth: "300px",
    borderRadius: "8px",
    left: "120px",
    fontWeight: "400",
    boxShadow: `
      0px 1px 8px rgba(0, 0, 0, 0.2),
      0px 3px 4px rgba(0, 0, 0, 0.12),
      0px 3px 3px rgba(0, 0, 0, 0.14)
    `,
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
  },
  popper: {
    zIndex: 1300,
  },
}))

const CompanyUserInfo = ({
  image,
  openPhotoPopup,
  anotherUser,
  // onOpenChat,
  name,
  // onChangeNameAndRole,
  onDeletePhotoProfile,
  activity,
  describeMe,
  descriptionProp,
  updateCompanyUser,
  markets,
  classifications,
  certifications,
  companyType,
  onOpenChat,
  isHaveChatButton,
  reported,
  onReport,
  industr,
  diverse,
  isBlocked,
  userRole,
  onBlock,
  eliteTag,
  verificationTag,
}) => {

  const tooltipClasses = useTooltipStyles()
  const sectorRef = useRef(null)
  const industryRef = useRef(null)
  const [isEditing, setIsEditing] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [isPopupOpened, setIsPopupOpened] = useState(false)
  const [description, setDescription] = useState("")
  const [certification, setCertification] = useState("")
  const [classification, setClassification] = useState("")
  const [market, setMarket] = useState("")
  const [companyName, setCompanyName] = useState("")
  const [errors, setErrors] = useState({})
  const [dropdownData, setDropdownData] = useState({
    activity: {},
    describeMe: {},
    industry: {},
    diverse: {},
  })

  const visibleVerificationTag =
    verificationTag === "Industry Verified" ? null : verificationTag

  const getEliteTooltipText = (tag) => {
    switch(tag) {
      case 'Elite':
        return 'Elite - business enterprise with revenue below $10M'
      case 'Elite Plus':
        return 'Elite Plus - business enterprise with revenue $10M - $50M'
      case 'Elite Premium':
        return 'Elite Premium - business enterprise with revenue above $50M'
      default:
        return ''
    }
  }

  const getVerificationBadge = (tag) => {
    switch (tag) {
      case "Salesforce Verified":
        return {
          imgSrc: "/img/Salesforce-verified.png",
          label: "Salesforce Verified",
          padded: true,
        }
      case "B2T Verified":
      default:
        return {
          imgSrc: "/img/B2T-verified.png",
          label: "B2T Verified",
          padded: "b2t",
        }
    }
  }

  // Filter out "All" option for profile edit (only show in search filters)
  const sectorOptions = companyRoleTypeOptions
    .filter((option) => option.value !== "All")
    .map((option) => ({
      ...option,
      databaseName: option.id,
    }))

  const activityOptions = activityFilterOptions
    .filter((option) => option.id !== "")
    .map((option) => ({
      ...option,
      databaseName: option.id,
    }))

  // Filter out "All" option for profile edit (only show in search filters)
  const companyIndustryOptions = companyIndustryTypeOptions
    .filter((option) => option.value !== "All")
    .map((option) => ({
      ...option,
      databaseName: option.id,
    }))

  const describeMeValue =
    dropdownData.describeMe.value &&
    sectorOptions.find(
      (item) => item.value === dropdownData.describeMe.value,
    )

  const activityValue =
    dropdownData?.activity?.value &&
    activityOptions.find(
      (item) => item?.value === dropdownData?.activity?.value,
    )

  const descr =
    sectorOptions.find((item) => item.databaseName === describeMe) ||
    ""
  const act = activity
    ? activityOptions.find((item) => item.databaseName === activity) || {}
    : {}
  const industry = industr
    ? companyIndustryOptions.find((item) => item.databaseName === industr)
    : ""
  const _diverse = diverse
    ? diverseOptions.find((item) => item.databaseName === diverse)
    : ""
  useEffect(() => {
    if (descr || activity || industr || diverse) {
      setDropdownData({
        ...dropdownData,
        industry: industry,
        describeMe: descr,
        activity: act,
        diverse: _diverse,
      })
    }
    if (descriptionProp) {
      setDescription(descriptionProp)
    }
    if (name) {
      setCompanyName(name)
    }
    if (certifications) {
      setCertification(certifications)
    }
    if (classifications) {
      setClassification(classifications)
    }
    if (markets) {
      setMarket(markets)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activity,
    descriptionProp,
    name,
    industr,
    certifications,
    classifications,
    markets,
  ])

  const checkToSave = () => {
    return (
      description.length === 0 ||
      Object.values(errors).some((item) => item.valid === false)
    )
  }

  const validateAndScrollToError = () => {
    const newErrors = { ...errors }
    
    // Validate sector
    const sectorError = sectorValidation(describeMeValue)
    if (!sectorError.valid) {
      newErrors.sector = sectorError
      setErrors(newErrors)
      // Scroll to sector field after state update
      setTimeout(() => {
        if (sectorRef.current) {
          sectorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return false
    }
    
    // Validate industry
    const industryError = industryValidation(dropdownData.industry)
    if (!industryError.valid) {
      newErrors.industry = industryError
      setErrors(newErrors)
      // Scroll to industry field after state update
      setTimeout(() => {
        if (industryRef.current) {
          industryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return false
    }
    
    return true
  }

  const checkNoDescription = () => {
    setErrors({
      ...errors,
      description: decriptionValidation(description),
    })
  }

  const handleCancel = () => {
    const data = objectDeepCopy(dropdownData)
    setIsEditing(false)
    setHasChanges(false)
    setIsPopupOpened(false)
    setDropdownData({
      ...data,
      describeMe: descr || "",
      activity: act || "",
      industry: industry || "",
      diverse: _diverse || {},
    })
    setDescription(descriptionProp || "")
    setCompanyName(name)
    setErrors({})
  }

  const onCancelClick = () => {
    if (hasChanges) {
      setIsPopupOpened(true)
    } else {
      setIsEditing(false)
      setErrors({})
    }
  }

  const handleSave = async () => {
    // Validate sector before saving
    if (!validateAndScrollToError()) {
      return
    }
    
    await updateCompanyUser({
      description: description,
      name: companyName,
      describe_company: dropdownData.describeMe?.databaseName || "",
      activity: dropdownData.activity?.databaseName || "",
      industry: dropdownData.industry?.databaseName || "",
      diverse: dropdownData.diverse?.databaseName || "",
      certifications: certification,
      classifications: classification,
      markets: market,
    })
    setIsEditing(false)
    setHasChanges(false)
  }

  const handleChangeInput = (e) => {
    setHasChanges(true)
    switch (e.target.name) {
      case "description": {
        setDescription(e.target.value)
        setErrors({
          ...errors,
          description: decriptionValidation(e.target.value),
        })
        break
      }
      case "certification": {
        setCertification(e.target.value)
        break
      }
      case "companyName": {
        setCompanyName(e.target.value)
        setErrors({
          ...errors,
          companyName: nameValidaton(e.target.value),
        })
        break
      }
      default:
        console.log("Wrong name")
    }
  }

  const handleDropdownChange = (name, value) => {
    setHasChanges(true)
    const data = objectDeepCopy(dropdownData)
    setDropdownData({
      ...data,
      [name]: value,
    })
    if (name === "describeMe") {
      if (value?.databaseName !== "financial_organizations") {
        setDropdownData((prev) => ({
          ...prev,
          [name]: value,
          activity: {},
        }))
      }
      setErrors({
        ...errors,
        sector: {
          valid: true,
        },
      })
    }

    if (name === 'activity' && value) {
      setErrors({
        ...errors,
        activity: {
          valid: true,
        },
      })
    }
  }

  const handleIndustryChange = (name, value) => {
    setHasChanges(true)
    const data = objectDeepCopy(dropdownData)
    setDropdownData({
      ...data,
      [name]: value,
    })
    // Clear industry error when a value is selected
    if (value) {
      setErrors({
        ...errors,
        industry: {
          valid: true,
        },
      })
    }
  }

  const handleDiverseChange = (name, value) => {
    handleIndustryChange(name, value)
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  return (
    <div className={`${styles.userInfoContainer} ${!isEditing && eliteTag ? styles.hasEliteTag : ''}`}>
      {!isEditing && eliteTag && (
        <div className={styles.eliteTagContainer}>
          <Tooltip 
            title={getEliteTooltipText(eliteTag)} 
            arrow 
            placement="top-end"
            classes={tooltipClasses}
            PopperProps={{
              modifiers: {
                offset: {
                  enabled: true,
                  offset: "130, -40",
                },
                flip: {
                  enabled: false,
                },
                preventOverflow: {
                  enabled: true,
                  boundariesElement: "viewport",
                },
              },
            }}
          >
            <div className={styles.eliteTagBadge}>
              {eliteTag}
            </div>
          </Tooltip>
        </div>
      )}
      <ReportButton
        isBlocked={isBlocked}
        userRole={userRole}
        onUnblock={() => onBlock(false)}
        onReport={onReport}
        onBlock={() => onBlock(true)}
        isReported={reported}
        anotherUser={anotherUser}
        className={styles.reportIcon}
      />
      <Prompt
        when={hasChanges}
        message={messages.CONFIRM_DISCARD_FORM_CHANGES_ON_PAGE_LEAVE}
      />

      {isPopupOpened && hasChanges && (
        <ConfirmCancelPopup
          handleCancel={handleCancel}
          setIsPopupOpened={setIsPopupOpened}
        />
      )}

      <div
        className={classNames(
          styles.imageContainer,
          !isEditing && visibleVerificationTag ? styles.imageContainerWithVerification : null,
        )}
      >
        <UserImage
          image={image}
          onUpdate={openPhotoPopup}
          selectedUser={anotherUser}
          onDelete={onDeletePhotoProfile}
        />

        {!isEditing && visibleVerificationTag && (() => {
          const verificationBadge = getVerificationBadge(visibleVerificationTag)

          return (
            <div className={styles.verificationUnderPhoto}>
              <span className={styles.verificationText}>
                {verificationBadge.label}
              </span>
              <img
                className={classNames(
                  styles.verificationLogo,
                  verificationBadge.padded === true && styles.verificationLogoPadded,
                  verificationBadge.padded === "b2t" && styles.verificationLogoB2t,
                )}
                src={verificationBadge.imgSrc}
                alt=""
                aria-hidden="true"
              />
            </div>
          )
        })()}
      </div>

      {isEditing ? (
        <div className={styles.inputsWrapper}>
          <Input
            name="companyName"
            error={errors.companyName && !errors.companyName.valid}
            errorMessage={errors.companyName && errors.companyName.errorMessage}
            value={companyName}
            onChange={handleChangeInput}
            fieldStyle="black"
            label="Company/Organization Name"
            className={styles.editItem}
          />
          <div ref={sectorRef}>
            <Dropdown
              id="01"
              name="describeMe"
              fieldStyle="black"
              options={sectorOptions}
              onBlur={() => {}}
              onChange={handleDropdownChange}
              value={describeMeValue || {}}
              label="Choose your sector"
              className={styles.editItem}
              error={errors.sector && !errors.sector.valid}
              errorMessage={errors.sector && errors.sector.errorMessage}
            />
          </div>
          {describeMeValue?.databaseName === "financial_organizations" && (
            <Dropdown
              id="02"
              name="activity"
              error={errors.activity && !errors.activity.valid}
              errorMessage={errors.activity && errors.activity.errorMessage}
              fieldStyle="black"
              options={activityOptions}
              onBlur={() => {}}
              onChange={handleDropdownChange}
              value={activityValue || {}}
              label="Choose your current main activity"
              className={styles.editItem}
            />
          )}
          <div ref={industryRef}>
            <Dropdown
              id="02"
              name="industry"
              fieldStyle="black"
              options={companyIndustryOptions}
              onBlur={() => {}}
              onChange={handleIndustryChange}
              value={dropdownData.industry || {}}
              label="Choose your industry:"
              className={styles.editItem}
              error={errors.industry && !errors.industry.valid}
              errorMessage={errors.industry && errors.industry.errorMessage}
            />
          </div>
          <Dropdown
            id="02"
            name="diverse"
            fieldStyle="black"
            options={diverseOptions}
            onBlur={() => {}}
            onChange={handleDiverseChange}
            value={dropdownData.diverse || {}}
            label="Choose if you are diverse:"
            className={styles.editItem}
          />
          <MultiSelectDropdown
            id="classification-select"
            name="classification"
            customPlaceholder="Select multiple options"
            fieldStyle="black"
            options={businessClassificationOptions}
            selectedOptions={classification}
            onBlur={() => {}}
            onChangeOption={(selectedOptions) =>
              setClassification(selectedOptions)
            }
            label="Choose your demographics:"
            className={styles.editItem}
          />
          <TagFinderView
            tagList={market.split(",").filter((item) => Boolean(item))}
            onUpdateTagList={(marketList) => setMarket(marketList.join())}
          />
          <TextArea
            vertical
            name="certification"
            value={certification}
            label="Business certification(s)"
            placeholder="Add certifications..."
            maxLength={1000}
            currentLength={certification.length}
            onChange={handleChangeInput}
            style={styles.textArea}
          />
          <TextArea
            vertical
            name="description"
            error={errors.description && !errors.description.valid}
            errorMessage={errors.description && errors.description.errorMessage}
            value={description}
            placeholder="Add Description..."
            maxLength={1000}
            currentLength={description.length}
            onFocus={checkNoDescription}
            onChange={handleChangeInput}
            style={styles.textArea}
          />
          <div className={styles.buttonsWrapper}>
            <SaveCancelButtons
              style={styles}
              ableToSave={checkToSave()}
              handleSave={handleSave}
              onCancelClick={onCancelClick}
            />
          </div>
        </div>
      ) : (
        <CompanyUserShowInfo
          name={companyName}
          companyType={companyType}
          anotherUser={anotherUser}
          description={description}
          market={market
            .split(",")
            .filter((item) => Boolean(item))
            .map((item) => {
              const { value: region = "", subValue = [] } =
                marketOptions.find(
                  (option) => option.databaseName === item.substring(0, 2),
                ) ?? {}
              const { name: subRegion = "" } =
                subValue.find(
                  (option) => option.databaseName === item.substring(2, 4),
                ) ?? {}
              return subRegion ? `${subRegion}, ${region}` : region
            })
            .join(", ")}
          classification={classification
            .split(",")
            .map(
              (item) =>
                businessClassificationOptions.find(
                  (option) => option.databaseName === item,
                )?.value,
            )
            .join(", ")}
          certification={certification}
          style={styles}
          dropdownData={dropdownData}
          handleEdit={handleEdit}
          onOpenChat={onOpenChat}
          isHaveChatButton={isHaveChatButton}
        />
      )}
    </div>
  )
}

CompanyUserInfo.defaultProps = {
  name: "Company Name",
  activity: "",
  describeMe: "",
}

CompanyUserInfo.propTypes = {
  image: PropTypes.string,
  openPhotoPopup: PropTypes.func,
  anotherUser: PropTypes.bool,
  name: PropTypes.string,
  onDeletePhotoProfile: PropTypes.func,
  activity: PropTypes.string,
  describeMe: PropTypes.string,
  descriptionProp: PropTypes.string,
  updateCompanyUser: PropTypes.func,
  companyType: PropTypes.string,
  eliteTag: PropTypes.string,
  verificationTag: PropTypes.string,
}

export default CompanyUserInfo
